import React, { useState, useMemo, useCallback } from "react";
import MySkeleton from "components/ui/feedback/Skeleton";
import { useHttpClient } from "shared/hooks/http-hook";
import { useStatsFetch } from "./useStatsApi";
import { SectionCard, ErrorMessage, SimpleTable, Badge, formatNumber } from "./StatComponents";

const REFERENCE_LABELS = {
  clients: "Panel Kullanıcısı",
  schools: "Okul",
  users: "Kişi (Veli/Personel/Sürücü)",
  students: "Öğrenci",
  studentSchoolHistory: "Öğrenci Okul Geçmişi",
  sms: "SMS",
  stocks: "Stok",
  stockMovements: "Stok Hareketi",
  fuel: "Yakıt",
  receipts: "Tahsilat",
  contracts: "Sözleşme",
  relations: "İlişki",
  personelServices: "Personel Servisi",
  vehicleRoutes: "Öğrenci Servisi",
  expenses: "Gider",
  shifts: "Vardiya",
  processes: "İşlem (Process)",
};

const selectStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "14px",
  background: "#fff",
};

const ReferenceResult = ({ result, isLoading, title }) => {
  if (isLoading) return <MySkeleton lineCount={4} />;
  if (!result) return null;

  return (
    <div style={{ marginTop: "15px" }}>
      <div
        style={{
          padding: "10px 14px",
          background: "#e8eaf6",
          borderRadius: "6px",
          fontSize: "13px",
          marginBottom: "12px",
        }}
      >
        <strong>{title}</strong> için toplam <strong>{formatNumber(result.total)}</strong> kayıt bulundu.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {result.references.map((ref) => (
          <span
            key={ref.key}
            style={{
              padding: "6px 12px",
              borderRadius: "4px",
              fontSize: "13px",
              background: ref.count > 0 ? "#e3f2fd" : "#f5f5f5",
              border: ref.count > 0 ? "1px solid #90caf9" : "1px solid #e0e0e0",
              color: ref.count > 0 ? "#0d47a1" : "#999",
            }}
          >
            <strong>{formatNumber(ref.count)}</strong> {REFERENCE_LABELS[ref.key] || ref.key}
          </span>
        ))}
      </div>
    </div>
  );
};

const ReferencesSection = () => {
  const { sendRequest } = useHttpClient();
  const { data: listData, isLoading: isListLoading, error: listError } = useStatsFetch(
    "/admin/statistics/schools-companies"
  );

  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companyResult, setCompanyResult] = useState(null);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState(null);

  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [schoolResult, setSchoolResult] = useState(null);
  const [isSchoolLoading, setIsSchoolLoading] = useState(false);
  const [schoolError, setSchoolError] = useState(null);

  const [schoolSearch, setSchoolSearch] = useState("");

  const companies = listData?.companies || [];
  const schools = listData?.schools || [];

  const fetchCompanyReferences = useCallback(
    async (companyId) => {
      setSelectedCompanyId(companyId);
      setCompanyResult(null);
      setCompanyError(null);
      if (!companyId) return;
      setIsCompanyLoading(true);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/statistics/company-references/${companyId}`
        );
        setCompanyResult(responseData);
      } catch (err) {
        setCompanyError(err?.message || "Şirket referansları alınamadı.");
      } finally {
        setIsCompanyLoading(false);
      }
    },
    [sendRequest]
  );

  const fetchSchoolReferences = useCallback(
    async (schoolId) => {
      setSelectedSchoolId(schoolId);
      setSchoolResult(null);
      setSchoolError(null);
      if (!schoolId) return;
      setIsSchoolLoading(true);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/statistics/school-references/${schoolId}`
        );
        setSchoolResult(responseData);
      } catch (err) {
        setSchoolError(err?.message || "Okul referansları alınamadı.");
      } finally {
        setIsSchoolLoading(false);
      }
    },
    [sendRequest]
  );

  const filteredSchools = useMemo(() => {
    const term = schoolSearch.trim().toLowerCase();
    if (!term) return schools;
    return schools.filter(
      (s) => s.name.toLowerCase().includes(term) || s.company.toLowerCase().includes(term)
    );
  }, [schools, schoolSearch]);

  return (
    <div>
      <ErrorMessage message={listError} />

      {isListLoading && !listData ? (
        <MySkeleton lineCount={8} />
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", marginBottom: "20px" }}>
            <div style={{ flex: "1 1 400px", minWidth: "300px" }}>
              <SectionCard title="Şirket Referans Sorgusu">
                <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
                  Seçilen şirketin tüm koleksiyonlardaki kayıt sayılarını gösterir.
                </p>
                <select
                  value={selectedCompanyId}
                  onChange={(e) => fetchCompanyReferences(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Şirket seçin...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.isDemo ? " (Demo)" : ""}
                      {!c.isActive ? " (Pasif)" : ""} — {c.schoolCount} okul
                    </option>
                  ))}
                </select>
                <ErrorMessage message={companyError} />
                <ReferenceResult
                  result={companyResult}
                  isLoading={isCompanyLoading}
                  title={companyResult?.company?.name}
                />
              </SectionCard>
            </div>

            <div style={{ flex: "1 1 400px", minWidth: "300px" }}>
              <SectionCard title="Okul Referans Sorgusu">
                <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
                  Seçilen okulun tüm koleksiyonlardaki kayıt sayılarını gösterir.
                </p>
                <select
                  value={selectedSchoolId}
                  onChange={(e) => fetchSchoolReferences(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Okul seçin...</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} — {s.company}
                    </option>
                  ))}
                </select>
                <ErrorMessage message={schoolError} />
                <ReferenceResult
                  result={schoolResult}
                  isLoading={isSchoolLoading}
                  title={schoolResult?.school?.name}
                />
              </SectionCard>
            </div>
          </div>

          <SectionCard
            title={`Okul → Şirket Listesi (${formatNumber(filteredSchools.length)} okul)`}
            actions={
              <input
                type="text"
                placeholder="Okul veya şirket ara..."
                value={schoolSearch}
                onChange={(e) => setSchoolSearch(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "13px",
                  minWidth: "220px",
                }}
              />
            }
          >
            <SimpleTable
              columns={[
                { key: "name", label: "Okul" },
                { key: "type", label: "Tür", render: (row) => <Badge>{row.type}</Badge> },
                { key: "company", label: "Şirket" },
                {
                  key: "id",
                  label: "ID",
                  render: (row) => <span style={{ color: "#999", fontSize: "12px" }}>{row.id}</span>,
                },
              ]}
              rows={filteredSchools}
              emptyText="Okul bulunamadı."
              rowKey={(row) => row.id}
            />
          </SectionCard>
        </>
      )}
    </div>
  );
};

export default ReferencesSection;
